/**
 * 手動登錄收款(REQ-20260807003)。
 *
 * 應收沖帳畫面原本只看得到「還欠多少」,收了錢沒有地方登錄 ——
 * 沖帳紀錄只有 mdb 匯入那一條路徑。這裡補上缺的那一塊:
 * 勾未結單據 → 每筆預帶入未結金額(可改成部分收款)→ 填收款日期與收款方式 → 送出。
 *
 * 金額只是「要沖多少」的意圖,後端會拿當下的未結餘額重算與驗證;
 * 這裡的檢查純粹是為了不要讓使用者按下去才發現超收。
 */
import { useEffect, useRef, useState } from 'react';
import type { CreateArSettlementInput, SalesOrderListItem } from '@app/shared';
import { Modal } from '../../components/Modal';
import { formatMoney } from '../../lib/format-money';
import { useCreateArSettlement } from '../accounting/api';

export interface ReceiptModalProps {
  open: boolean;
  onClose: () => void;
  customerId: number;
  /** 未結應收明細,由 `useArOutstanding()` 帶進來,不在這裡重查 */
  outstanding: SalesOrderListItem[];
  /**
   * 開場先幫忙勾起來的單號(REQ-20260815001,訪談頁的「收款」深連結)。
   * 只是替使用者按了一次那個 checkbox,沖帳邏輯完全沒有第二套。
   */
  defaultPickedOrderNo?: string | undefined;
  /**
   * 「查看收款沖帳紀錄」的去處(REQ-20260815004)。彈窗蓋住了底下的沖帳紀錄,
   * 給一條直接過去的路;沒給就不顯示這個連結。彈窗本身不查沖帳紀錄。
   */
  onViewHistory?: (() => void) | undefined;
}

export function ReceiptModal({
  open,
  onClose,
  customerId,
  outstanding,
  defaultPickedOrderNo,
  onViewHistory,
}: ReceiptModalProps): JSX.Element {
  const mutation = useCreateArSettlement(customerId);
  /** 勾選的單號 → 這次要沖的金額(字串,維持 decimal 精度) */
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [settledAt, setSettledAt] = useState(todayInTaipei);
  const [cashAmount, setCashAmount] = useState('');
  const [checkAmount, setCheckAmount] = useState('');
  /** 支票兌現日期 / 匯款帳號末 4 碼(REQ-20260817006),都不與金額互相要求 */
  const [checkClearDate, setCheckClearDate] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferAccountLast4, setTransferAccountLast4] = useState('');
  const [usePrepaid, setUsePrepaid] = useState('');

  const rows = outstanding.map((o) => ({ order: o, amount: picked[o.orderNo] }));
  const selected = rows.filter((r) => r.amount !== undefined);
  const total = sumAmounts(selected.map((r) => r.amount ?? '0'));
  // 超收的單直接標出來 —— 後端也會擋,但先擋在這裡才不用等一次來回
  const overpaid = selected.filter((r) => Number(r.amount) > Number(r.order.outstanding));
  const invalid = selected.filter((r) => !(Number(r.amount) > 0));

  /** 單號 → 該筆的 tr,深連結預先勾選後要捲到它(REQ-20260824003) */
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  /**
   * 深連結指定的那一筆,在彈窗打開時預先勾起來(REQ-20260815001)——
   * 等同替使用者按了一次那個 checkbox(未結金額一併預帶入)。
   *
   * 只套用一次:使用者自己取消勾選之後,不該被 effect 又勾回去。
   * 找不到(單號不存在 / 已收完款而不在未結清單 / 屬於別的客戶)就什麼都不做,
   * 彈窗照常打開、清單照常顯示,只是沒有預先勾選的那一筆。
   *
   * 勾好之後捲到那一列(REQ-20260824003):未結明細依交易日期由舊到新排,
   * 深連結帶進來的通常是剛建立的新單 → 排在最後面,而新分頁預設停在最上方,
   * 使用者看不到自己要沖的是哪一筆。用 rAF 等這次渲染跑完才捲,理由同
   * REQ-20260815004 的 onViewHistory:同一個 tick 捲的話版面還沒定案,位置會算錯。
   */
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (!open || defaultApplied.current || defaultPickedOrderNo == null) return;
    const target = outstanding.find((o) => o.orderNo === defaultPickedOrderNo);
    if (!target) return;
    defaultApplied.current = true;
    setPicked({ [target.orderNo]: target.outstanding });
    requestAnimationFrame(() => {
      rowRefs.current[target.orderNo]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [open, defaultPickedOrderNo, outstanding]);

  const toggle = (order: SalesOrderListItem): void => {
    setPicked((prev) => {
      const next = { ...prev };
      // 勾起來時預帶入該單的未結金額,要部分收款再自己改
      if (next[order.orderNo] === undefined) next[order.orderNo] = order.outstanding;
      else delete next[order.orderNo];
      return next;
    });
  };

  const submit = (): void => {
    const input: CreateArSettlementInput = {
      settledAt,
      cashAmount: cashAmount.trim() || null,
      checkAmount: checkAmount.trim() || null,
      checkClearDate: checkClearDate.trim() || null,
      transferAmount: transferAmount.trim() || null,
      transferAccountLast4: transferAccountLast4.trim() || null,
      usePrepaid: usePrepaid.trim() || null,
      addPrepaid: null,
      lines: selected.map((r) => ({
        sourceDocNo: r.order.orderNo,
        settledAmount: r.amount ?? '0',
      })),
    };
    mutation.mutate(input, {
      onSuccess: () => {
        setPicked({});
        setCashAmount('');
        setCheckAmount('');
        setCheckClearDate('');
        setTransferAmount('');
        setTransferAccountLast4('');
        setUsePrepaid('');
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="登錄收款"
      dismissOnBackdrop={false}
      widthClass="max-w-3xl"
      headerNote={
        onViewHistory ? (
          <button
            type="button"
            data-testid="receipt-view-history"
            onClick={onViewHistory}
            className="text-sm text-blue-600 hover:underline"
          >
            查看收款沖帳紀錄 →
          </button>
        ) : undefined
      }
    >
      <div className="grid gap-4">
        {outstanding.length === 0 ? (
          <div className="text-slate-500 text-sm">
            這個客戶目前沒有未結的應收款項,不需要登錄收款。
          </div>
        ) : (
          <>
            <table className="w-full bg-white shadow-sm rounded-md">
              <thead>
                <tr className="border-b text-left text-sm text-slate-600">
                  <th className="px-4 py-2 w-10"> </th>
                  <th className="px-4 py-2">日期</th>
                  <th className="px-4 py-2">單號</th>
                  <th className="px-4 py-2 text-right">未結</th>
                  <th className="px-4 py-2 text-right">本次沖銷</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ order, amount }) => (
                  <tr
                    key={order.id}
                    ref={(el) => {
                      rowRefs.current[order.orderNo] = el;
                    }}
                    // 勾起來的那一列整列標色(REQ-20260824003)——深連結預先勾的、
                    // 使用者自己勾的一視同仁。emerald 是這張表沒用過的色階
                    // (紅色是超收警示、藍色是送出鈕)。hover 一併換成同色系的深一階,
                    // 否則滑過去會被 hover:bg-slate-50 蓋掉,看起來像取消勾選。
                    className={`border-b last:border-0 text-sm ${
                      amount !== undefined ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-slate-50'
                    }`}
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        aria-label={`選擇 ${order.orderNo}`}
                        data-testid={`receipt-pick-${order.orderNo}`}
                        checked={amount !== undefined}
                        onChange={() => toggle(order)}
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{order.txnDate}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-mono">{order.orderNo}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatMoney(order.outstanding)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`${order.orderNo} 沖銷金額`}
                        data-testid={`receipt-amount-${order.orderNo}`}
                        disabled={amount === undefined}
                        value={amount ?? ''}
                        onChange={(e) =>
                          setPicked((prev) => ({ ...prev, [order.orderNo]: e.target.value }))
                        }
                        className="border rounded-md px-2 py-1 text-sm w-28 text-right font-mono disabled:bg-slate-100"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="grid gap-1">
                <label className="text-sm text-slate-600" htmlFor="receipt-settled-at">
                  收款日期
                </label>
                <input
                  id="receipt-settled-at"
                  type="date"
                  data-testid="receipt-settled-at"
                  value={settledAt}
                  onChange={(e) => setSettledAt(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <MoneyField
                id="receipt-cash"
                label="現金"
                value={cashAmount}
                onChange={setCashAmount}
              />
              <MoneyField
                id="receipt-check"
                label="支票"
                value={checkAmount}
                onChange={setCheckAmount}
              />
              <div className="grid gap-1">
                <label className="text-sm text-slate-600" htmlFor="receipt-check-clear-date">
                  支票兌現日期
                </label>
                <input
                  id="receipt-check-clear-date"
                  type="date"
                  data-testid="receipt-check-clear-date"
                  value={checkClearDate}
                  onChange={(e) => setCheckClearDate(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <MoneyField
                id="receipt-transfer"
                label="匯款"
                value={transferAmount}
                onChange={setTransferAmount}
              />
              <div className="grid gap-1">
                <label className="text-sm text-slate-600" htmlFor="receipt-transfer-last4">
                  帳號末4碼
                </label>
                <input
                  id="receipt-transfer-last4"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  data-testid="receipt-transfer-last4"
                  value={transferAccountLast4}
                  onChange={(e) => setTransferAccountLast4(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm font-mono"
                />
              </div>
              <MoneyField
                id="receipt-use-prepaid"
                label="動用預收款"
                value={usePrepaid}
                onChange={setUsePrepaid}
              />
            </div>

            <p className="text-xs text-slate-500">
              收款方式金額是「錢怎麼進來的」註記,沖帳合計一律以上方勾選的明細加總為準。
              談好不收的尾款請走出貨單的「折讓」,不要在這裡少沖。
            </p>

            {overpaid.length > 0 ? (
              <div role="alert" className="text-red-600 text-sm">
                {overpaid.map((r) => r.order.orderNo).join('、')} 的沖銷金額超過未結餘額。
              </div>
            ) : null}
            {mutation.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                登錄失敗：
                {mutation.error instanceof Error ? mutation.error.message : '未知錯誤'}
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-sm text-slate-600">
                已選 {selected.length} 筆,沖帳合計{' '}
                <span data-testid="receipt-total" className="font-mono text-slate-900">
                  {formatMoney(total)}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded border hover:bg-slate-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  data-testid="receipt-submit"
                  onClick={submit}
                  disabled={
                    selected.length === 0 ||
                    overpaid.length > 0 ||
                    invalid.length > 0 ||
                    mutation.isPending
                  }
                  className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {mutation.isPending ? '登錄中...' : '確認收款'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function MoneyField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label className="text-sm text-slate-600" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        data-testid={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 text-sm text-right font-mono"
      />
    </div>
  );
}

/** 顯示用的合計。權威金額由後端以 Decimal 重算,這裡只是給人看的即時回饋。 */
function sumAmounts(amounts: string[]): string {
  const cents = amounts.reduce((sum, a) => sum + Math.round((Number(a) || 0) * 100), 0);
  return (cents / 100).toFixed(2);
}

/** 收款日期預設今天(台北)—— 容器與瀏覽器時區都不一定是台北,明確指定 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
